import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Social-proof ticker for the Funeral Professional landing page.
 *
 * Shows the live count of branded obituaries funeral directors have
 * generated through the program (from `/api/fd/public-stats`), with a
 * baseline floor so the number reads as meaningful on day one. The
 * label says "branded obituaries generated by funeral professionals"
 * — reframed from the previous consumer-site families-served metric,
 * which is the wrong audience signal for B2B directors.
 *
 * On mount it tweens from ~65% of the final value up to the target
 * via `requestAnimationFrame`, matching the existing tick-up feel.
 */

// The number is reframed into the B2B audience; floor tuned so the
// component always reads as legitimate social proof even before the
// B2B side has meaningful volume. Bump this over time.
const FLOOR = 3200;

const FdFamiliesTicker = () => {
  const [displayed, setDisplayed] = useState(0);
  const [target, setTarget] = useState(null);
  const rafRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    axios
      .get('/api/fd/public-stats')
      .then((res) => {
        if (cancelled) return;
        const obits = Number(res?.data?.obituaries || 0);
        setTarget(Math.max(FLOOR, obits));
      })
      .catch(() => {
        if (!cancelled) setTarget(FLOOR);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (target == null) return;
    const start = Math.max(0, Math.floor(target * 0.65));
    const duration = 1600;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out-cubic
      const val = Math.round(start + (target - start) * eased);
      setDisplayed(val);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target]);

  if (target == null) return null;

  return (
    <div className="fd-families-ticker" aria-live="polite">
      <div className="fd-families-ticker-dot" aria-hidden="true">
        <span />
      </div>
      <div className="fd-families-ticker-text">
        <span className="fd-families-ticker-value">
          ~{displayed.toLocaleString()}+
        </span>
        <span className="fd-families-ticker-label">
          branded obituaries generated by funeral professionals per month
        </span>
      </div>
    </div>
  );
};

export default FdFamiliesTicker;
